import { useState, useEffect } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { translations } from '../translations';

const ContactPage = () => {
  const [formState, setFormState] = useState({
    name: '',
    email: '',
    company: '',
    phone: '',
    message: '',
    subject: 'General Inquiry'
  });
  
  const [submitted, setSubmitted] = useState(false);
  const { language } = useLanguage();
  const t = translations[language]?.contactPage || {};

  useEffect(() => {
    document.title = 'Sanction Screener - Contact';
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormState(prev => ({
      ...prev,
      [name]: value
    }));
  };
  
  const handleSubmit = (e) => {
    e.preventDefault();
    // In a real implementation, you would submit the form data to your backend
    console.log('Form submitted:', formState);
    setSubmitted(true);
    // Reset form after submission
    setFormState({
      name: '',
      email: '',
      company: '',
      phone: '',
      message: '',
      subject: 'General Inquiry'
    });
  };

  return (
    <div>
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-blue-800 to-indigo-900 text-white py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl font-bold mb-6">{t.heroTitle || 'Contact Us'}</h1>
            <p className="text-xl text-blue-100">
              {t.heroSubtitle || "Have questions or need assistance? Our team is here to help you."}
            </p>
          </div>
        </div>
      </section>

      {/* Contact Form & Info Section */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            {/* Contact Form */}
            <div className="bg-white rounded-xl shadow-lg p-8">
              {submitted ? (
                <div className="text-center py-16">
                  <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-green-100 text-green-500 mb-6">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <h3 className="text-2xl font-bold text-gray-800 mb-4">{t.thankYou || 'Thank You!'}</h3>
                  <p className="text-gray-600 mb-6">
                    {t.successMessage || "Your message has been sent successfully. We'll get back to you as soon as possible."}
                  </p>
                  <button 
                    onClick={() => setSubmitted(false)}
                    className="inline-block py-3 px-6 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg transition duration-200"
                  >
                    {t.sendAnother || 'Send Another Message'}
                  </button>
                </div>
              ) : (
                <>
                  <h2 className="text-2xl font-bold text-gray-800 mb-6">{t.sendUsMessage || 'Send Us a Message'}</h2>
                  <form onSubmit={handleSubmit}>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                      <div>
                        <label htmlFor="name" className="block text-gray-700 font-medium mb-2">{t.nameLabel || 'Your Name *'}</label>
                        <input
                          type="text"
                          id="name"
                          name="name"
                          value={formState.name}
                          onChange={handleChange}
                          className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 outline-none transition"
                          required
                        />
                      </div>
                      <div>
                        <label htmlFor="email" className="block text-gray-700 font-medium mb-2">{t.emailLabel || 'Your Email *'}</label>
                        <input
                          type="email"
                          id="email"
                          name="email"
                          value={formState.email}
                          onChange={handleChange}
                          className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 outline-none transition"
                          required
                        />
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                      <div>
                        <label htmlFor="company" className="block text-gray-700 font-medium mb-2">{t.companyLabel || 'Company'}</label>
                        <input
                          type="text"
                          id="company"
                          name="company"
                          value={formState.company}
                          onChange={handleChange}
                          className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 outline-none transition"
                        />
                      </div>
                      <div>
                        <label htmlFor="phone" className="block text-gray-700 font-medium mb-2">{t.phoneLabel || 'Phone Number'}</label>
                        <input
                          type="tel"
                          id="phone"
                          name="phone"
                          value={formState.phone}
                          onChange={handleChange}
                          className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 outline-none transition"
                        />
                      </div>
                    </div>
                    
                    <div className="mb-6">
                      <label htmlFor="subject" className="block text-gray-700 font-medium mb-2">{t.subjectLabel || 'Subject *'}</label>
                      <select
                        id="subject"
                        name="subject"
                        value={formState.subject}
                        onChange={handleChange}
                        className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 outline-none transition"
                        required
                      >
                        <option value="General Inquiry">{t.subjectGeneral || 'General Inquiry'}</option>
                        <option value="Sales Question">{t.subjectSales || 'Sales Question'}</option>
                        <option value="Technical Support">{t.subjectTechnical || 'Technical Support'}</option>
                        <option value="Partnership Opportunity">{t.subjectPartnership || 'Partnership Opportunity'}</option>
                        <option value="Media Inquiry">{t.subjectMedia || 'Media Inquiry'}</option>
                      </select>
                    </div>
                    
                    <div className="mb-6">
                      <label htmlFor="message" className="block text-gray-700 font-medium mb-2">{t.messageLabel || 'Your Message *'}</label>
                      <textarea
                        id="message"
                        name="message"
                        value={formState.message}
                        onChange={handleChange}
                        rows="5"
                        className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 outline-none transition"
                        required
                      ></textarea>
                    </div>
                    
                    <div>
                      <button 
                        type="submit"
                        className="w-full py-3 px-6 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg transition duration-200"
                      >
                        {t.sendButton || 'Send Message'}
                      </button>
                    </div>
                  </form>
                </>
              )}
            </div>
            
            {/* Contact Info */}
            <div>
              <h2 className="text-2xl font-bold text-gray-800 mb-6">{t.contactInfoTitle || 'Contact Information'}</h2>
              
              <div className="mb-12">
                <h3 className="text-xl font-semibold text-gray-800 mb-4">{t.ourOfficesTitle || 'Our Offices'}</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  <div className="bg-gray-50 p-6 rounded-lg">
                    <h4 className="font-bold text-gray-800 mb-2">{t.officeZagreb || 'Zagreb'}</h4>
                    <p className="text-gray-600 mb-4">
                      {t.officeZagrebAddress || 'Radnička cesta 50'}<br />
                      {t.officeZagrebCity || '10000 Zagreb, Croatia'}
                    </p>
                    <p className="text-gray-600">
                      <strong>{t.phoneLabelShort || 'Phone:'}</strong> +49 30 1234 5678<br />
                      <strong>{t.emailLabelShort || 'Email:'}</strong> info@sanctionscreener.eu
                    </p>
                  </div>
                  <div className="bg-gray-50 p-6 rounded-lg">
                    <h4 className="font-bold text-gray-800 mb-2">{t.officeLondon || 'London'}</h4>
                    <p className="text-gray-600 mb-4">
                      {t.officeLondonAddress || 'Lister House Lister Hill'}<br />
                      {t.officeLondonCity || 'Horsforth, Leeds, England, LS18 5AZ'}
                    </p>
                    <p className="text-gray-600">
                      <strong>{t.phoneLabelShort || 'Phone:'}</strong> +32 2 789 4321<br />
                      <strong>{t.emailLabelShort || 'Email:'}</strong> info@sanctionscreener.eu
                    </p>
                  </div>
                </div>
              </div>
              
              <div className="mb-12">
                <h3 className="text-xl font-semibold text-gray-800 mb-4">{t.supportChannelsTitle || 'Support Channels'}</h3>
                <div className="space-y-6">
                  <div className="flex items-start">
                    <div className="flex-shrink-0 mr-4">
                      <div className="bg-blue-100 p-3 rounded-full">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                        </svg>
                      </div>
                    </div>
                    <div>
                      <h4 className="text-lg font-medium text-gray-800 mb-1">{t.emailSupportTitle || 'Email Support'}</h4>
                      <p className="text-gray-600 mb-1">{t.generalInquiries || 'For general inquiries:'}</p>
                      <a href="mailto:info@sanctionsscreener.eu" className="text-blue-600 hover:underline">info@sanctionscreener.eu</a>
                      <p className="text-gray-600 mb-1 mt-2">{t.technicalSupport || 'For technical support:'}</p>
                      <a href="mailto:support@sanctionsscreener.eu" className="text-blue-600 hover:underline">support@sanctionscreener.eu</a>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContactPage; 